import { memo } from 'react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import GlassmorphButton from '../../ui/GlassmorphButton'
import AvatarUploadField from './AvatarUploadField'
import { labelClasses, getInputClasses, errorMessageClasses, getSelectClasses } from '../../../styles/inputStyles'

/**
 * ProfileEditForm - Profile information editing form
 * Handles name, email, language, and avatar updates
 */
const ProfileEditForm = memo(function ProfileEditForm({
  user,
  formData,
  errors,
  isSaving,
  avatarPreview,
  avatarError,
  onSubmit,
  onChange,
  onCancel,
  onAvatarChange,
  onRemoveAvatar,
  onAvatarError,
  t,
  i18n
}) {
  return (
    <div className="mt-4 p-3 bg-white/70 backdrop-blur-sm rounded-md border border-gray-200/50">
      <form onSubmit={onSubmit} className="space-y-3">
        {/* Avatar Upload */}
        <AvatarUploadField
          user={user}
          avatarPreview={avatarPreview}
          avatarError={avatarError}
          onAvatarChange={onAvatarChange}
          onRemoveAvatar={onRemoveAvatar}
          onAvatarError={onAvatarError}
          t={t}
        />

        {/* First Name */}
        <div>
          <label htmlFor="firstName" className={labelClasses}>
            {t('profile.firstName', 'First Name')}
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={onChange}
            className={getInputClasses(errors.firstName)}
            required
          />
          {errors.firstName && (
            <p className={errorMessageClasses}>{errors.firstName}</p>
          )}
        </div>

        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className={labelClasses}>
            {t('profile.lastName', 'Last Name')}
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={onChange}
            className={getInputClasses(errors.lastName)}
            required
          />
          {errors.lastName && (
            <p className={errorMessageClasses}>{errors.lastName}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className={labelClasses}>
            {t('profile.email', 'Email')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={onChange}
            className={getInputClasses(errors.email)}
            required
          />
          {errors.email && (
            <p className={errorMessageClasses}>{errors.email}</p>
          )}
        </div>

        {/* Language Selector */}
        <div>
          <label htmlFor="language" className={labelClasses}>
            {t('profile.language', 'Language')}
          </label>
          <select
            id="language"
            name="language"
            value={formData.language || i18n.language}
            onChange={onChange}
            className={getSelectClasses()}
          >
            <option value="en">English</option>
            <option value="es-MX">Español (México)</option>
            <option value="fr-CA">Français (Canada)</option>
            <option value="pt-BR">Português (Brasil)</option>
            <option value="ru">Русский</option>
            <option value="zh-CN">中文 (简体)</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <GlassmorphButton
            type="submit"
            variant="primary"
            icon={CheckIcon}
            disabled={isSaving}
            size="sm"
            className="flex-1 px-3 py-2 text-xs font-medium"
          >
            {isSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </GlassmorphButton>
          <GlassmorphButton
            type="button"
            variant="secondary"
            icon={XMarkIcon}
            onClick={onCancel}
            disabled={isSaving}
            size="sm"
            className="flex-1 px-3 py-2 text-xs font-medium"
          >
            {t('common.cancel', 'Cancel')}
          </GlassmorphButton>
        </div>
      </form>
    </div>
  )
})

export default ProfileEditForm
